import * as next from '../../../types/next'
import { CONTROL_NAME } from '../../../utils'
import type { UpBlockHandler } from '../types'

import { upCommonSettings } from './common'

export const up: UpBlockHandler<next.MeterReadingBlock> = (uischema) => {
  if (
    uischema.type !== CONTROL_NAME.METER_READING_CONTROL &&
    uischema.type !== CONTROL_NAME.METER_READING_DYNAMIC_CONTROL
  ) {
    throw new Error(
      'Expected a valid Meter reading or Dynamic Meter Reading control'
    )
  }

  const mode = uischema.options?.entity_updating_mode
    ? next.MeterReadingBlockMode.ENTITY_UPDATE
    : next.MeterReadingBlockMode.STANDARD_INPUT

  if (mode == next.MeterReadingBlockMode.STANDARD_INPUT) {
    return {
      type: next.BLOCK_TYPE.METER_READING,
      settings: {
        ...upCommonSettings(uischema),
        meter: {
          marketLocationId: upMeterReadingField(
            uischema.options?.fields?.maloId
          ),
          meterId: upMeterReadingField(uischema.options?.fields?.meterId),
          counterTypeChoices: upCounterTypeChoices(
            uischema.options?.fields?.meterType
          ),
          unit: upMeterReadingUnit(uischema.options?.fields?.readingValue?.unit)
        },
        fields: {
          date: upMeterReadingField(uischema.options?.fields?.readingDate),
          by: upMeterReadingField(uischema.options?.fields?.readBy),
          reason: upMeterReadingField(uischema.options?.fields?.reason)
        },
        mode: mode
      }
    } as next.StandardMeterReadingBlock
  }

  return {
    type: next.BLOCK_TYPE.METER_READING,
    settings: {
      ...upCommonSettings(uischema),
      fields: {
        date: upMeterReadingField(uischema.options?.fields?.readingDate),
        by: upMeterReadingField(uischema.options?.fields?.readBy),
        reason: upMeterReadingField(uischema.options?.fields?.reason)
      },
      mode: mode
    }
  } as next.EntityUpdatingMeterReadingBlock
}

function upCounterTypeChoices(
  meterType:
    | {
        required?: boolean
        showOneDirectional?: boolean
        showTwoDirectional?: boolean
        showBiDirectional?: boolean
      }
    | undefined
): next.StandardMeterReadingBlock['settings']['meter']['counterTypeChoices'] {
  // When meter type is not enabled, the meter reading should be with the default ONE_TARIFF counter
  if (!meterType) {
    return [next.TARIFF_TYPE.ONE_TARIFF]
  }

  const choices: next.TARIFF_TYPE[] = []

  // If any of the choices are selected add them to the choices list
  if (meterType.showOneDirectional) {
    choices.push(next.TARIFF_TYPE.ONE_TARIFF)
  }
  if (meterType.showTwoDirectional) {
    choices.push(next.TARIFF_TYPE.TWO_TARIFF)
  }
  if (meterType.showBiDirectional) {
    choices.push(next.TARIFF_TYPE.BI_DIRECTIONAL)
  }

  // if meter type exists but no choices are selected, the counter should be the default ONE_TARIFF
  if (choices.length === 0) {
    return [next.TARIFF_TYPE.ONE_TARIFF]
  }

  // Ensure the return type is a tuple with at least one element
  return choices as next.StandardMeterReadingBlock['settings']['meter']['counterTypeChoices']
}

function upMeterReadingField(field: { required?: boolean } | undefined) {
  return {
    display: field ? true : false,
    required: field?.required ? true : false
  }
}

export function upMeterReadingUnit(unit: { show: boolean; label: string }) {
  const unitInput = unit?.label

  if (!unitInput) {
    // When the unit is not defined or not valid, we default to kWh (same a the renderer)
    return next.UNITS.kwh
  }

  if (Object.keys(next.UNITS).includes(unitInput)) {
    return next.UNITS[unitInput as keyof typeof next.UNITS] as string
  }
}
